/**
 * Local fast-path cache for CabView sidebar UI state.
 *
 * The authoritative copy lives in the server session (restored via
 * getInitCabviewData), but that arrives seconds after first paint — without a
 * local mirror the panel flashes its defaults (collapsed tab) and then pops to
 * the restored state. This cache hydrates redux synchronously at load so the
 * first paint matches the last session; the server restore then reconciles
 * (including the permission-pruned filters), and every persist write-through
 * keeps the two in sync. UI preferences only — nothing sensitive.
 */

const CACHE_KEY = 'cabviewUiState';

/**
 * @return {Object} cached { sidebarOpen, groupStates, filters, listViewMode } or {}
 */
export function readCabviewUiCache() {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (err) {
    return {};
  }
}

/**
 * Patch-merge values into the cache.
 * @param {Object} patch subset of { sidebarOpen, groupStates, filters, listViewMode }
 */
export function writeCabviewUiCache(patch) {
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify({...readCabviewUiCache(), ...patch}));
  } catch (err) {
    // Storage full/blocked — the session copy still persists everything.
  }
}

/**
 * Drop the cache entirely. This key isn't scoped per user/customer, so it must
 * be cleared on login/logout or filters (e.g. vehicle serials) from a
 * previous account's session bleed into the next one.
 */
export function clearCabviewUiCache() {
  try {
    window.localStorage.removeItem(CACHE_KEY);
  } catch (err) {
    // Storage blocked — nothing to clean up.
  }
}
